// src/hooks/usePendingQuestions.ts
//
// Phase 3.7.7 — Pending-questions client hook.
//
// Provides:
//   - questions[]: open pending_questions for the current user,
//     ordered oldest-first (FIFO) so newer questions queue
//     behind older unanswered ones in the modal stack.
//   - openCount: just questions.length, exposed separately so
//     the badge can subscribe without re-rendering on each
//     question detail change.
//   - isLoading: true until the first snapshot has arrived.
//   - isDisabled: true when the user has set
//     pending_questions_disabled (so we don't show the (?)
//     icon at all).
//   - answer(question_id, answer_text): submit a free-text or
//     formatted answer. The server records it and enqueues a
//     curator candidate that will become a high-provenance
//     explicit_statement memory.
//   - skip(question_id): no-op locally; the question stays
//     status='open' so it'll come back in a later session.
//   - dismiss(question_id, reason): hard-dismiss this single
//     question. Used internally; the public modal only exposes
//     skip per-card. The footer's "Give me a break" uses
//     setPendingQuestionsEnabled(false) instead, which clears
//     all open questions in one server-side step.
//   - setEnabled(enabled): top-level toggle. When false, the
//     server auto-dismisses any open questions and refuses new
//     emissions until re-enabled. Pulls the badge to 0
//     immediately.
//
// Note on transport: pending_questions are server-only writes
// (rules) and the answer flow MUST go through the
// answerPendingQuestion HTTPS callable so the curator gets the
// answer as a high-provenance candidate. We never write to
// pending_questions directly from the client.

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../services/firebase';

export type PendingQuestionAnswerFormat =
  | 'free_text'
  | 'single_select'
  | 'multi_select'
  | 'yes_no'
  | 'custom';

export interface PendingQuestion {
  id: string;
  question: string;
  intent: string;
  topic_key?: string;
  related_memory_id?: string;
  status: 'open' | 'answered' | 'dismissed' | 'superseded';
  created_at: Date | null;
  emitted_by_trigger?: string;

  // Phase 3.7.7
  answer_format?: PendingQuestionAnswerFormat;
  options?: string[];
}

interface AnswerCallableResponse {
  ok: boolean;
  question_id: string;
  enqueued_queue_item_id: string | null;
  status: string;
}

interface DismissCallableResponse {
  ok: boolean;
  question_id: string;
}

interface SetEnabledCallableResponse {
  ok: boolean;
  pending_questions_disabled: boolean;
  auto_dismissed_count: number;
}

interface UsePendingQuestionsResult {
  questions: PendingQuestion[];
  openCount: number;
  isLoading: boolean;
  isDisabled: boolean;
  answer: (questionId: string, answerText: string) => Promise<void>;
  skip: (questionId: string) => void;
  dismiss: (questionId: string, reason?: string) => Promise<void>;
  setEnabled: (enabled: boolean) => Promise<void>;
}

export const usePendingQuestions = (): UsePendingQuestionsResult => {
  const [questions, setQuestions] = useState<PendingQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);

  // Subscribe to the user doc for the disabled flag.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        const data = snap.data();
        setIsDisabled(data?.pending_questions_disabled === true);
      },
      // Fail-open on any read error; treat as enabled so the
      // user can still interact if there's a transient
      // permissions/network blip.
      () => setIsDisabled(false)
    );
    return () => unsub();
  }, []);

  // Subscribe to open pending questions.
  // We deliberately AVOID an orderBy here so that we don't need
  // a composite index — the open set is small (cap of 8) so
  // sorting client-side is cheap.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }
    const ref = collection(db, 'users', user.uid, 'pending_questions');
    const q = query(ref, where('status', '==', 'open'));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const out: PendingQuestion[] = [];
        snapshot.forEach((d) => {
          const v = d.data();
          out.push({
            id: d.id,
            question: String(v.question || ''),
            intent: String(v.intent || ''),
            topic_key: v.topic_key || undefined,
            related_memory_id: v.related_memory_id || undefined,
            status: 'open',
            created_at:
              v.created_at instanceof Timestamp
                ? v.created_at.toDate()
                : null,
            emitted_by_trigger: v.emitted_by_trigger || undefined,
            answer_format:
              (v.answer_format as PendingQuestionAnswerFormat) || 'free_text',
            options:
              Array.isArray(v.options) && v.options.length > 0
                ? (v.options.filter(
                    (o: unknown) => typeof o === 'string'
                  ) as string[])
                : undefined,
          });
        });
        // Oldest first — FIFO so older unanswered questions
        // surface before newer ones.
        out.sort((a, b) => {
          const ta = a.created_at?.getTime() ?? 0;
          const tb = b.created_at?.getTime() ?? 0;
          return ta - tb;
        });
        setQuestions(out);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
    return () => unsub();
  }, []);

  const answer = useCallback(
    async (questionId: string, answerText: string) => {
      const callable = httpsCallable<
        { question_id: string; answer_text: string },
        AnswerCallableResponse
      >(functions, 'answerPendingQuestion');
      await callable({ question_id: questionId, answer_text: answerText });
    },
    []
  );

  const skip = useCallback((_questionId: string) => {
    // Skip is a client-side affordance only. The question stays
    // status='open' on the server so it returns in a later
    // session. We don't make a server call.
  }, []);

  const dismiss = useCallback(
    async (questionId: string, reason?: string) => {
      const callable = httpsCallable<
        { question_id: string; reason?: string },
        DismissCallableResponse
      >(functions, 'dismissPendingQuestion');
      await callable({ question_id: questionId, reason });
    },
    []
  );

  const setEnabled = useCallback(async (enabled: boolean) => {
    const callable = httpsCallable<
      { enabled: boolean },
      SetEnabledCallableResponse
    >(functions, 'setPendingQuestionPreference');
    await callable({ enabled });
  }, []);

  return {
    questions,
    openCount: questions.length,
    isLoading,
    isDisabled,
    answer,
    skip,
    dismiss,
    setEnabled,
  };
};
